import { Timestamp } from 'firebase/firestore';
import { Utensils, Martini, Users, type LucideIcon } from 'lucide-react';
import type { ConversationData, ChatUser } from './types';
import { getDateLocale } from '@/utils/dateUtils';
import i18n from '@/lib/i18n';

// Single source of truth for the per-conversation-type icon, shared by the
// conversation list and the conversation header so they always match.
export function getConversationIcon(conversation: ConversationData): LucideIcon {
  if (conversation.tableId || conversation.kind === 'table') return Utensils;
  if (conversation.kind === 'kiki' || conversation.kind === 'kiki_request') return Users;
  return Martini;
}

export function getDisplayName(
  senderId: string,
  userId: string | null,
  userCache: Record<string, ChatUser>
): string {
  if (senderId === 'system') return 'Outclose';
  if (senderId === userId) return 'You';

  // Try user cache
  const cached = userCache[senderId];
  if (cached?.name) {
    return cached.name;
  }

  return 'Unknown';
}

export function getConversationTitle(
  conversation: ConversationData,
  userId: string | null,
  userCache: Record<string, ChatUser>
): string {
  if (conversation.title) {
    return conversation.title;
  }

  // Group chats are name-only — never reveal participant names if title is missing.
  if (conversation.kind === 'group') {
    return 'Group chat';
  }

  const otherParticipants = conversation.participants.filter((p) => p !== userId);
  const names = otherParticipants.map((p) => {
    return userCache[p]?.name || 'Unknown';
  });

  return names.join(', ') || 'Conversation';
}

export function getConversationSubtitle(conversation: ConversationData): string | undefined {
  return conversation.subtitle;
}

export function formatTime(timestamp: Timestamp | Date | null): string {
  if (!timestamp) return '';
  
  const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  const locale = getDateLocale();

  if (diffDays === 0) {
    return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  } else if (diffDays === 1) {
    return i18n.t('common.time.yesterday');
  } else if (diffDays < 7) {
    return date.toLocaleDateString(locale, { weekday: 'short' });
  } else {
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  }
}

